import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Printer, RefreshCw } from "lucide-react";
import { PageHeader } from "../components/Primitives";
import api from "../lib/api";
import { toast } from "sonner";

// iter323 — In-app Help / Training Center. Serves markdown quick-start
// guides shipped in `/app/backend/training_docs/` via the `/api/help`
// endpoints. Users land on the tab matching their role; every tab is
// browsable so a supervisor training a new guard can preview any doc.
// The Print button opens a browser print dialog against a print-only
// stylesheet so ops can save any guide as PDF without a server
// conversion pipeline.

const TABS = [
  { key: "admin", title: "Admin", subtitle: "Agency setup, users, payroll" },
  { key: "ops", title: "Ops / Supervisor", subtitle: "Roster, tardies, dispatch" },
  { key: "guard", title: "Guard / Officer", subtitle: "Clock-in, DARs, incidents" },
  { key: "client", title: "Client", subtitle: "Portal, reports, coverage" },
];

export default function Help() {
  const [defaultKey, setDefaultKey] = useState("admin");
  const [activeKey, setActiveKey] = useState("admin");
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/help/docs");
        setDefaultKey(res.data?.default || "admin");
        setActiveKey(res.data?.default || "admin");
      } catch {
        setActiveKey("admin");
      }
    })();
  }, []);

  useEffect(() => {
    if (!activeKey) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/help/docs/${activeKey}`);
        setMarkdown(res.data?.markdown || "");
      } catch (e) {
        toast.error("Failed to load help doc");
        setMarkdown("");
      } finally {
        setLoading(false);
      }
    })();
  }, [activeKey]);

  const onReplayTour = async () => {
    try {
      await api.post("/help/tour-reset");
      toast.success("Tour will fire on next login (or refresh)");
    } catch {
      toast.error("Couldn't reset tour");
    }
  };

  const onPrint = () => {
    // Open the print-only route in a new tab; the user's browser saves
    // to PDF via its native print dialog. Keeps zero server deps.
    window.open(`/help/print/${activeKey}`, "_blank", "noopener");
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="help-page">
      <PageHeader
        eyebrow="Help / Training"
        title="Training Center"
        subtitle="Role-specific quick-start guides. Print any doc as PDF for onboarding packets."
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="help-role-tabs">
          {TABS.map((t) => {
            const on = t.key === activeKey;
            const isDefault = t.key === defaultKey;
            return (
              <button
                key={t.key}
                data-testid={`help-tab-${t.key}`}
                onClick={() => setActiveKey(t.key)}
                className={`px-3 py-2 text-left border transition-colors duration-150 ${
                  on
                    ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                    : "border-[#27272A] text-zinc-400 hover:border-[#FF3B30]"
                }`}
              >
                <div className="text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5">
                  {t.title}
                  {isDefault && (
                    <span className="text-[9px] font-normal opacity-75">· yours</span>
                  )}
                </div>
                <div className={`text-[10px] mt-0.5 ${on ? "text-white/80" : "text-zinc-500"}`}>{t.subtitle}</div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="help-replay-tour-button"
            onClick={onReplayTour}
            className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#0A84FF] hover:text-[#0A84FF] px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-400 transition-colors"
          >
            <RefreshCw className="size-3" /> Replay Tour
          </button>
          <button
            data-testid="help-print-button"
            onClick={onPrint}
            className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-400 transition-colors"
          >
            <Printer className="size-3" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div
        data-testid="help-content"
        className="border border-[#27272A] bg-[#121212] p-6 md:p-10 max-w-4xl help-markdown"
      >
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : (
          <ReactMarkdown>{markdown}</ReactMarkdown>
        )}
      </div>
      {/* Markdown styling scoped to the help viewer. Kept inline so this
          component is self-contained. */}
      <style>{`
        .help-markdown h1 { font-family: inherit; font-weight: 800; font-size: 1.75rem; letter-spacing: -0.02em; margin-bottom: 0.75rem; color: #fff; }
        .help-markdown h2 { font-weight: 700; font-size: 1.1rem; margin-top: 2rem; margin-bottom: 0.5rem; color: #fff; letter-spacing: -0.01em; }
        .help-markdown h3 { font-weight: 700; font-size: 0.95rem; margin-top: 1.5rem; margin-bottom: 0.35rem; color: #fff; }
        .help-markdown p, .help-markdown li { color: #d4d4d8; font-size: 0.9rem; line-height: 1.65; }
        .help-markdown ul, .help-markdown ol { padding-left: 1.25rem; margin: 0.5rem 0; }
        .help-markdown li { margin: 0.25rem 0; }
        .help-markdown strong { color: #fff; font-weight: 700; }
        .help-markdown em { color: #a1a1aa; }
        .help-markdown code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.82rem; padding: 0.1rem 0.35rem; background: #0A0A0A; border: 1px solid #27272A; color: #FF9F0A; }
        .help-markdown hr { border-color: #27272A; margin: 2rem 0; }
        .help-markdown a { color: #0A84FF; text-decoration: underline; }
      `}</style>
    </div>
  );
}
